/** @jsxImportSource @emotion/react */
import { css, useTheme } from '@emotion/react';
import { SelectOption } from 'models/formSingleSelectField';
import { RouteComponentProps } from '@reach/router';
import instance, { ApiResponse, isAxiosErrorHandled } from 'api';
import Button from 'components/button/Button';
import FormTextField from 'components/form/FormTextField';
import { RadioButton } from 'components/radiobuttons/radiobuttons';
import {
    completeUIUpdate,
    NotificationIdentifier,
    selectUIUpdate,
} from 'components/notifications/notificationUIUpdateReducer';
import { endpoints } from 'endpoints.config';
import { useFormikContext } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { ProductType } from 'components/sideMenu/SideMenu';
import { BrandVisual } from 'components/brandVisual/BrandVisual';
import { getBrandKey } from 'helpers/brandIdentity';
import { selectCompanyName, selectImagePath } from 'reducers/ui';
import { Toast, ToastMessageReason } from 'helpers/toast';
import { AccountType } from './AccountPage';
import { WrappedFormSingleSelectField } from 'components/form/FormSingleSelectField';
import { useIsSubledgerAccount } from 'helpers/useIsSubledgerAccount';
import { useGetFiatAccountDetails } from 'helpers/useGetFiatAccountDetails';
import { ManualCollapse } from 'components/Collapse/Collapse';
import { ReactComponent as PrintIcon } from '../../assets/Icon-print.svg';

export type Props = {
    accountNumber: string;
    accountType: string;
};

type SavedCard = {
    id: number;
    cardholderName: string;
    cardDescription: string;
};

type FormValues = {
    cardType: 'New card' | 'Saved card' | null;
    customerAssetAccountsId: number;
    amount?: number;
    bSaveCard: boolean;
    paymentCardsId: number;
    cvV2: string;
};

const companyName = localStorage?.getItem("companyName")

const contentDispositionRegex = /.*?filename="?(.*?\.pdf)"?/; // for some reason the endpoint sometimes has content type filename:"file" and sometimes filename:file

type DownloadOption = { value: 'reference' | 'details'; label: string };

export const PayIn: React.FC<Props & RouteComponentProps> = ({
    accountNumber,
    navigate,
    accountType,
}) => {
    const [iframeUrl, setIframeUrl] = useState('');
    const uiUpdate = useSelector(selectUIUpdate);
    const dispatch = useDispatch();
    const imagesPath = useSelector(selectImagePath);
    const companyName = useSelector(selectCompanyName);
    const brandKey = getBrandKey(imagesPath, companyName);
    const { t } = useTranslation('dashboard');
    const { t: commonT } = useTranslation('common');
    const [pdfDownloading, setPdfDownloading] = useState(false);
    const [selectedDownloadOption, setSelectedDownloadOption] = useState<DownloadOption | null>(null);

    const downloadOptions: DownloadOption[] = [
        { value: 'reference', label: t('bankReferenceLetter') },
        { value: 'details', label: t('accountDetailsDocument') },
    ];

    useEffect(() => {
        if (
            uiUpdate?.pushType === NotificationIdentifier.CLOSE_DALPAY_IFRAME &&
            uiUpdate.data.guid === ''
        ) {
            setIframeUrl('');
            if (!uiUpdate.data.bVoid && navigate) navigate('../transactions');
            dispatch(completeUIUpdate());
        }
    }, [uiUpdate, dispatch, navigate]);

    const handleDownloadAccountDetails = () => {
        setPdfDownloading(true);

        instance
            .get<BlobPart>(endpoints.accounts.getAccountDetailsPdf, {
                params: {
                        CustomerAssetAccountsId: accountNumber,
                    },
                responseType: 'blob',
            })
            .then((res) => {
                const todayDate = new Date().toISOString().split('T')[0];
                const fileName = `Account_Details_${todayDate}.pdf`;
                const downloadUrl = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
            })
            .catch(async (error) => {
                const isJsonResponse =
                    error.response.headers['content-type']?.includes('application/json');

                if (isJsonResponse && isAxiosErrorHandled(error)) {
                    const text = await (error.response.data as any).text();
                    const json: ApiResponse = JSON.parse(text);
                    Toast.openErrorToast(
                        json.errors?.[0]?.messageCode ?? 'Unable to download statement'
                    );
                } else {
                    Toast.openErrorToast('Unable to download statement');
                }
            })
            .finally(() => {
                setPdfDownloading(false);
            });
    };

    const handleDownloadReferenceLetter = () => {
            setPdfDownloading(true);
    
            instance
                .get<BlobPart>(endpoints.accounts.getReferenceLetterPdf, {
                    params: {
                        CustomerAssetAccountsId: accountNumber,
                    },
                    responseType: 'blob',
                })
                .then((res) => {
                    const todayDate = new Date().toISOString().split('T')[0];
                    const fileName = `Bank_Reference_Letter_${todayDate}.pdf`;
                    const downloadUrl = window.URL.createObjectURL(new Blob([res.data]));
                    const link = document.createElement('a');
                    link.href = downloadUrl;
                    link.setAttribute('download', fileName);
                    document.body.appendChild(link);
                    link.click();
                })
                .catch(async (error) => {
                    const isJsonResponse =
                        error.response.headers['content-type']?.includes('application/json');
    
                    if (isJsonResponse && isAxiosErrorHandled(error)) {
                        // Convert from blob response to json response
                        const text = await (error.response.data as any).text();
                        const json: ApiResponse = JSON.parse(text);
                        Toast.openErrorToast(json.errors?.[0]?.messageCode ?? 'Unable to download reference letter');
                    }
                    else {
                        Toast.openErrorToast('Unable to download reference letter');
                    }
                })
                .finally(() => {
                    setPdfDownloading(false);
                });
        }

    const handleDownloadSelected = () => {
        if (!selectedDownloadOption || pdfDownloading) return;

        // Clear selection while preparing so radios show no selection during download.
        setSelectedDownloadOption(null);

        if (selectedDownloadOption.value === 'reference') {
            handleDownloadReferenceLetter();
        } else if (selectedDownloadOption.value === 'details') {
            handleDownloadAccountDetails();
        }
    };

    return (
        <div className={`PayInPage PayInPage--${brandKey}`}>
            <div className="LeftSide">
                {iframeUrl ? (
                    <div className="IframeContainer">
                        <iframe title="Payment form" src={iframeUrl} height={500}></iframe>
                        <div className="IframePlaceholder notranslate" translate="no">{t('preparingPaymentForm')}</div>
                    </div>
                ) : (
                    <>
                        <p className="notranslate PayInTopUpInstruction" translate="no">
                            {t('topUpInstruction')}
                        </p>
                        <TransferDetails accountType={accountType} accountNumber={accountNumber} />
                        <p className="DocumentDownloadInstruction">{t('selectDocumentToDownload')}</p>
                        <div className="DocumentDownloadControls">
                            <div className="DocumentDownloadRadios">
                                {downloadOptions.map((opt) => (
                                    <RadioButton
                                        key={opt.value}
                                        label={opt.label}
                                        selected={selectedDownloadOption?.value === opt.value}
                                        disabled={pdfDownloading}
                                        onClick={() => setSelectedDownloadOption(opt)}
                                    />
                                ))}
                            </div>

                            <Button
                                variety="full"
                                color="first"
                                disabled={!selectedDownloadOption || pdfDownloading}
                                showSubmittingSpinner={pdfDownloading}
                                onClick={handleDownloadSelected}
                            >
                                {commonT('download')}
                            </Button>
                        </div>
                        <SubledgerDisclaimer
                            accountNumber={accountNumber}
                            accountType={accountType}
                        />

                    </>
                )}
            </div>
            <BrandVisual />
        </div>
    );
};

const SubledgerDisclaimer = ({ accountNumber }: Props) => {
    const isSubledger = useIsSubledgerAccount(parseInt(accountNumber));
    const { t } = useTranslation('dashboard');
    return isSubledger ? (
        <div className="SubledgerInfo notranslate" translate="no">
            <h3 className="head">
                <div className="ExclamationIcon">!</div>
                {t('additionalInformation')}
            </h3>
            <p>
                <Trans i18nKey="bankAccountNameMatch" ns="dashboard">
                The name on your bank account <u>must</u> match the name on your {companyName} account.
                </Trans>
            </p>
            <p>
                {t('depositsConversionWarning')}
            </p>
        </div>
    ) : null;
};

export const SavedCardForm = (props: {
    errorMessage?: string;
    plainForm?: boolean;
    cardListUrl?: string;
    accordion?: boolean;
    setCardPaymentErrMsg?: (err: string) => void;
}) => {
    const [savedCards, setSavedCards] = useState<SavedCard[]>([]);
    const { values, isSubmitting, setFieldValue } = useFormikContext<FormValues>();
    const dispatch = useDispatch();
    const { t } = useTranslation('dashboard');
    const cardDropdownOptions: SelectOption[] = savedCards.map((card) => ({
        value: card.id,
        label: `${card.cardDescription} - ${card.cardholderName}`,
    }));

    useEffect(() => {
        instance
            .get<ApiResponse<SavedCard[]>>(props.cardListUrl ?? endpoints.accounts.getPaymentCards)
            .then((res) => res.data.details && setSavedCards(res.data.details))
            .catch((err) =>
                Toast.openToastMessage(
                    t('couldNotLoadCards'),
                    ToastMessageReason.ERROR
                )
            );
    }, [dispatch, props.cardListUrl]);
    const form = (
        <div className="PayInForm">
            {values.cardType === 'Saved card' && (
                <div className="ExistingCardOptions">
                    <WrappedFormSingleSelectField
                        fieldName="paymentCardsId"
                        options={cardDropdownOptions}
                        label={t('chooseSavedCard')}
                        dropdownProps={{
                            /*
                className: 'CardSelect',
                classNamePrefix: 'CardSelect', */
                            placeholder: t('pleaseSelect'),
                        }}
                    />
                    <FormTextField field="cvV2" label={t('cvc')} required className="CvvInput" />
                </div>
            )}
            {!props.plainForm && (
                <FormTextField
                    field="amount"
                    label={t('amountToDeposit')}
                    required
                    inputMode="decimal"
                    type="number"
                />
            )}
            {props.errorMessage && <div className="ErrorLabel">{props.errorMessage}</div>}
            {!props.plainForm && (
                <Button disabled={isSubmitting} variety="full">
                    {values.cardType === 'New card' ? t('enterCardDetails') : t('deposit')}
                </Button>
            )}
            {props.plainForm && props.accordion && (
                <Button type="submit" variety="full" disabled={isSubmitting}>
                    {t('makePayment')}
                </Button>
            )}
        </div>
    );
    if (cardDropdownOptions.length === 0 && props.accordion) return null;
    return props.accordion ? (
        <ManualCollapse
            header={t('payWithSavedCard')}
            isOpen={values.cardType === 'Saved card'}
            setOpen={(open: boolean) => {
                setFieldValue('cardType', open ? 'Saved card' : null);
                props.setCardPaymentErrMsg && props.setCardPaymentErrMsg('');
            }}
        >
            {form}
        </ManualCollapse>
    ) : (
        <>{form}</>
    );
};

export const TransferDetails = (props: {
    accountNumber: string;
    accountType: string;
    style?: any;
    accountDetails?: any;
}) => {
    const theme = useTheme();
    const { t } = useTranslation('dashboard');
    const boxCSS = css`
        border: 1px solid ${theme.colors.first};
        color: ${theme.colors.first};
    `;
    const hookAccountDetails = useGetFiatAccountDetails();
    const accountDetails = props.accountDetails || hookAccountDetails;

    const isCRB = accountDetails?.productDisplayName === ProductType.CRB;
    const isDBS =
        accountDetails?.productDisplayName === ProductType.DBS ||
        accountDetails?.productDisplayName === ProductType.DBS_TMP;
    const isGLDB = accountDetails?.productDisplayName === ProductType.GLDB;
    const isDDA = accountDetails?.accountType === "DDA" && accountDetails?.accountType;
    const isSubledger = useIsSubledgerAccount(parseInt(props.accountNumber));
    
    return (
        <table className="TransferDetails" css={boxCSS} style={props.style || {}}>
            <tbody>
                <tr className="SectionHeading">
                    <th colSpan={2} className="notranslate" translate="no">{t('accountDetails')}</th>
                </tr>
                {accountDetails?.swiftBic && (
                    <tr>
                        <td className="Label notranslate" translate="no">{t('swiftBic')}</td>
                        <td className="Value notranslate" translate="no">{accountDetails.swiftBic}</td>
                    </tr>
                )}
                {!(accountDetails?.swiftBic && !accountDetails.routingNumber) && (
                    <tr>
                        <td className="Label notranslate" translate="no">{t('routingNumber')}</td>
                        <td className="Value notranslate" translate="no">{accountDetails?.routingNumber ?? 'XXXXXXXXX'}</td>
                    </tr>
                )}
                {!!accountDetails?.virtualAccountNumber ? (
                    <tr>
                        <td className="Label notranslate" translate="no">{t('iban')}</td>
                        <td className="Value notranslate" translate="no">{accountDetails.virtualAccountNumber}</td>
                    </tr>
                ) : (
                    <tr>
                        <td className="Label notranslate" translate="no">{t('accountNumber')}</td>
                        <td className="Value notranslate" translate="no">{accountDetails?.accountNumber ?? 'XXXXXXXXXX'}</td>
                    </tr>
                )}
                {accountDetails?.reference && (
                    <tr>
                        <td className="Label notranslate" translate="no">{t('reference')}</td>
                        <td className="Value notranslate" translate="no">{accountDetails.reference}</td>
                    </tr>
                )}
            </tbody>

            <tbody>
                <tr className="SectionHeading">
                    <th colSpan={2} className="notranslate" translate="no">{t('receivingBank')}</th>
                </tr>
                <tr>
                    <td className="Label notranslate" translate="no">{t('name')}</td>
                    <td className="Value notranslate" translate="no">{accountDetails?.productDisplayName}</td>
                </tr>
                <tr>
                    <td className="Label notranslate" translate="no">{t('address')}</td>
                    <td className="Value notranslate" translate="no">{accountDetails?.productAddress}</td>
                </tr>
            </tbody>

            <tbody>
                <tr className="SectionHeading">
                    <th colSpan={2} className="notranslate" translate="no">{t('remittanceInformation')}</th>
                </tr>
                {accountDetails?.vaName && (
                    <>
                        <tr>
                            <td className="Label notranslate" translate="no">{t('name')}</td>
                            <td className="Value notranslate" translate="no">{accountDetails.vaName}</td>
                        </tr>
                        <tr>
                            <td className="Label notranslate" translate="no">{t('paymentDetailsField')}</td>
                            <td className="Value notranslate" translate="no">
                                {isCRB
                                    ? `${companyName} ${companyName?.toLowerCase() === "ibanera" ? "LLC" : ""}`
                                    : isDBS
                                        ? `${companyName?.toLowerCase() === "ibanera" ? "'IBANERA PRIVATE LIMITED'" : companyName}`
                                        : isGLDB
                                            ? `${companyName?.toLowerCase() === "ibanera" ? "'IBANERA PRIVATE LIMITED'" : companyName}`
                                            : ''}
                            </td>
                        </tr>
                        {accountDetails.vaAddress && (
                            <tr>
                                <td className="Label notranslate" translate="no">{t('address')}</td>
                                <td className="Value notranslate" translate="no">{accountDetails.vaAddress}</td>
                            </tr>
                        )}
                    </>
                )}
                {!accountDetails?.vaName && (
                    <>
                        <tr>
                            <td className="Label notranslate" translate="no">{t('name')}</td>
                            <td className="Value notranslate" translate="no">
                                {(isSubledger || isDDA) ? accountDetails?.customerName : isCRB ? `${companyName} ${companyName?.toLowerCase() === "ibanera" ? "LLC" : ""}` : `${companyName?.toLowerCase() === "ibanera" ? "'IBANERA PRIVATE LIMITED'" : companyName}`}
                            </td>
                        </tr>
                        <tr>
                            <td className="Label notranslate" translate="no">{t('address')}</td>
                            <td className="Value notranslate" translate="no">
                                {(isSubledger || isDDA) ? accountDetails?.customerAddress : isCRB
                                    ? `${companyName?.toLowerCase() === "ibanera" ? "78 SW 7th St 7-118 Miami FL 3313" : "16192 COASTAL HIGHWAY LEWES, DELAWARE 19958"}`
                                    : isDBS
                                        ? `${companyName?.toLowerCase() === "ibanera" ? '62 Ubi Road 1, #03-16 Oxley Bizhub 2, Singapore 408734' : "16192 COASTAL HIGHWAY LEWES, DELAWARE 19958"}`
                                        : isGLDB
                                            ? `${companyName?.toLowerCase() === "ibanera" ? '62 Ubi Road 1, #03-16 Oxley Bizhub 2, Singapore 408734' : "16192 COASTAL HIGHWAY LEWES, DELAWARE 19958"}`
                                            : ''}                            
                            </td>
                        </tr>
                    </>
                )}
            </tbody>
        </table>
    );
};
